import { Fragment, useRef, useState } from "react";
import { useIsFetching, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Calculator, KeyRound, Lock, Plus, RefreshCw, Unlock, Wallet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ConfirmReasonDialog } from "@/components/common/ConfirmReasonDialog";
import { EmptyState, ErrorState, LoadingState, Notice, PageHeader, Spinner, StatusBadge } from "@/components/common/States";
import { ActionDot, useUnreadNotifications } from "@/components/layout/ActionDot";
import { api, errorMessage, fetcher } from "@/lib/api";
import { ACCESS_STATE, BILL_STATUS, RESELLER_STATUS, SETTLEMENT_STATUS, TENANT_STATUS, rupiah } from "@/lib/labels";
import { ActionDialog, ACTIONS } from "./BillingActionDialog";
import BillingActionQueue from "./BillingActionQueue";

const Section = ({ title, description, testId, children, actions }) => (
  <section className="rounded-2xl border bg-card p-6" data-testid={testId}>
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <h2 className="text-base md:text-lg font-bold">{title}</h2>
        {description && <p className="text-sm text-muted-foreground mt-1 max-w-2xl">{description}</p>}
      </div>
      {actions}
    </div>
    <div className="mt-4">{children}</div>
  </section>
);

const Stat = ({ label, value, hint, tone = "text-[#3B82F6]", testId }) => (
  <div className="rounded-2xl border bg-card p-5" data-testid={testId}>
    <p className={`font-brand text-2xl md:text-3xl font-extrabold ${tone}`}>{value}</p>
    <p className="text-sm font-medium mt-0.5">{label}</p>
    {hint && <p className="text-xs text-muted-foreground mt-1">{hint}</p>}
  </div>
);

const F = ({ id, label, hint, ...p }) => (
  <div className="space-y-2">
    <Label htmlFor={id}>{label}</Label>
    <Input id={id} {...p} />
    {hint && <p className="text-xs text-muted-foreground">{hint}</p>}
  </div>
);

// Angka rupiah yang belum ada datanya ditulis "—", bukan Rp0, supaya tidak terbaca sebagai tagihan nol.
const uang = (v) => (v === undefined || v === null ? "—" : rupiah(v));

const EMPTY_RESELLER = { name: "", contact_name: "", phone: "", email: "", qris_label: "", qris_nmid: "", bank_account: "", referral_code: "", retail_price_idr: "" };

function ResellerDialog({ open, onOpenChange, cap, onSaved }) {
  const [f, setF] = useState(EMPTY_RESELLER);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async () => {
    if (f.name.trim().length < 2) return setErr("Nama reseller wajib diisi, minimal 2 karakter.");
    if (f.retail_price_idr && !/^\d+$/.test(f.retail_price_idr)) return setErr("Harga jual harus angka rupiah tanpa titik, contoh 3000.");
    setBusy(true);
    setErr("");
    try {
      const body = Object.fromEntries(Object.entries(f).map(([k, v]) => [k, v === "" ? null : v]));
      if (body.retail_price_idr !== null) body.retail_price_idr = Number(body.retail_price_idr);
      const { data } = await api.post("/platform/billing/resellers", body);
      onSaved(data);
      toast.success(`${data.name} ditambahkan. Harga jual ${rupiah(data.retail_price_idr)} per akun per bulan.`);
      setF(EMPTY_RESELLER);
      onOpenChange(false);
    } catch (e) {
      setErr(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="reseller-create-dialog">
        <DialogHeader>
          <DialogTitle>Tambah Reseller</DialogTitle>
          <DialogDescription>Reseller menagih sekolah lewat QRIS miliknya lalu menyetor bagian founder.</DialogDescription>
        </DialogHeader>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="md:col-span-2"><F id="rs-name" label="Nama reseller *" value={f.name} onChange={set("name")} placeholder="Contoh: Bapak Hendra" data-testid="reseller-name-input" /></div>
          <F id="rs-contact" label="Nama kontak" value={f.contact_name} onChange={set("contact_name")} />
          <F id="rs-phone" label="Telepon/WA" value={f.phone} onChange={set("phone")} />
          <F id="rs-email" label="Email" type="email" value={f.email} onChange={set("email")} />
          <F id="rs-ref" label="Kode referral" hint="Kosong = dibuat otomatis." value={f.referral_code} onChange={set("referral_code")} />
          <F id="rs-qris" label="Label QRIS" placeholder="Strato" value={f.qris_label} onChange={set("qris_label")} />
          <F id="rs-nmid" label="NMID QRIS" placeholder="ID1026601636883" value={f.qris_nmid} onChange={set("qris_nmid")} />
          <F id="rs-bank" label="Rekening bank" value={f.bank_account} onChange={set("bank_account")} />
          <F id="rs-price" label={`Harga jual per akun (plafon ${rupiah(cap)})`} inputMode="numeric" placeholder="3000" value={f.retail_price_idr} onChange={set("retail_price_idr")} data-testid="reseller-price-input" />
        </div>
        {err && <Notice tone="danger" testId="reseller-create-error">{err}</Notice>}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="reseller-create-cancel">Batal</Button>
          <Button onClick={submit} disabled={busy} data-testid="reseller-create-submit">{busy && <Spinner />} Simpan reseller</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PriceDialog({ open, onOpenChange, reseller, cap, onSaved }) {
  const [value, setValue] = useState(reseller ? String(reseller.retail_price_idr) : "");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  if (!reseller) return null;

  const submit = async () => {
    if (!/^\d+$/.test(value)) return setErr("Harga jual harus angka rupiah tanpa titik, contoh 3000.");
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.patch(`/platform/billing/resellers/${reseller.id}`, { retail_price_idr: Number(value), reason: `Ditetapkan ${rupiah(Number(value))} dari Pusat Kendali` });
      onSaved(data);
      toast.success(`Harga jual ${data.name} kini ${rupiah(data.retail_price_idr)} per akun. Periode berjalan mempertahankan harga lama.`);
      onOpenChange(false);
    } catch (e) {
      setErr(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="price-dialog">
        <DialogHeader>
          <DialogTitle>Harga jual {reseller.name}</DialogTitle>
          <DialogDescription>Berlaku untuk tagihan periode berikutnya. Plafon sistem {rupiah(cap)} per akun per bulan.</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="price-value">Harga jual per akun per bulan</Label>
          <Input id="price-value" inputMode="numeric" value={value} onChange={(e) => setValue(e.target.value)} data-testid="price-value-input" />
          <p className="text-xs text-muted-foreground">Harga setoran ke founder tetap {rupiah(reseller.wholesale_price_idr)} dan tidak dapat diubah.</p>
        </div>
        {err && <Notice tone="danger" testId="price-dialog-error">{err}</Notice>}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Batal</Button>
          <Button onClick={submit} disabled={busy} data-testid="price-dialog-submit">{busy && <Spinner />} Simpan harga</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Satu reseller = satu akun login Ruang Mitra. Sandi ditampilkan sekali lalu tidak pernah lagi.
function ResellerAccountDialog({ open, onOpenChange, reseller, onSaved }) {
  const [email, setEmail] = useState("");
  const [fullName, setFullName] = useState("");
  const [password, setPassword] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [created, setCreated] = useState(null);
  if (!reseller) return null;
  const mode = reseller.login_email ? "reset" : "create";

  const close = (o) => {
    if (!o) {
      setCreated(null);
      setErr("");
      setPassword("");
      setReason("");
      setEmail("");
      setFullName("");
    }
    onOpenChange(o);
  };

  const usul = () => setPassword(`Sist${Math.floor(100000 + Math.random() * 900000)}`);

  const cekSandi = () => {
    if (password.length < 10) return "Kata sandi minimal 10 karakter dan berisi huruf serta angka.";
    if (!/[A-Za-z]/.test(password) || !/\d/.test(password)) return "Kata sandi harus berisi huruf dan angka.";
    return null;
  };

  const submit = async () => {
    if (mode === "create") {
      if (!email.trim() || !email.includes("@")) return setErr("Email akun wajib diisi dengan format yang benar.");
      const masalah = cekSandi();
      if (masalah) return setErr(masalah);
    } else {
      const masalah = cekSandi();
      if (masalah) return setErr(masalah);
      if (reason.trim().length < 5) return setErr("Alasan reset wajib diisi minimal 5 karakter.");
    }
    setBusy(true);
    setErr("");
    try {
      if (mode === "create") {
        const { data } = await api.post(`/platform/billing/resellers/${reseller.id}/akun`,
          { email: email.trim(), password, full_name: fullName.trim() || null });
        setCreated({ email: data.email, password });
        onSaved();
      } else {
        await api.post(`/platform/billing/resellers/${reseller.id}/akun/sandi`, { password, reason: reason.trim() });
        toast.success(`Sandi akun ${reseller.login_email} diganti. Semua sesi lama reseller otomatis keluar.`);
        close(false);
        onSaved();
      }
    } catch (e) {
      setErr(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent data-testid="reseller-account-dialog">
        <DialogHeader>
          <DialogTitle>{mode === "create" ? `Buat akun login ${reseller.name}` : `Ganti sandi akun ${reseller.login_email}`}</DialogTitle>
          <DialogDescription>
            {mode === "create"
              ? "Satu reseller cukup satu akun login untuk masuk ke Ruang Mitra (/mitra). Serahkan email dan sandi ini kepada reseller."
              : "Sandi baru menggantikan yang lama, dan semua sesi reseller yang masih terbuka otomatis keluar."}
          </DialogDescription>
        </DialogHeader>
        {created ? (
          <div className="space-y-4">
            <Notice tone="ok" testId="reseller-account-created">
              <p className="font-semibold">Akun dibuat. Catat sekarang — sandi tidak ditampilkan lagi:</p>
              <p className="mt-1 font-mono text-sm break-all" data-testid="reseller-account-credential">{created.email} / {created.password}</p>
            </Notice>
            <DialogFooter>
              <Button onClick={() => close(false)} data-testid="reseller-account-done">Selesai</Button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-4">
            {mode === "create" ? (
              <>
                <F id="acc-email" label="Email akun *" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="reseller@contoh.id" data-testid="reseller-account-email" />
                <F id="acc-name" label="Nama tampilan" value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder={`Default: ${reseller.name}`} />
              </>
            ) : (
              <div className="rounded-lg border px-3 py-2 text-sm">
                <p className="text-xs text-muted-foreground">Akun login</p>
                <p className="font-medium break-all" data-testid="reseller-account-existing-email">{reseller.login_email}</p>
              </div>
            )}
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <Label htmlFor="acc-pass">Kata sandi baru *</Label>
                <Button type="button" variant="link" size="sm" className="h-auto p-0 text-xs" onClick={usul} data-testid="reseller-account-suggest">Usulkan sandi</Button>
              </div>
              <Input id="acc-pass" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" data-testid="reseller-account-password" />
              <p className="text-xs text-muted-foreground">Minimal 10 karakter, campur huruf dan angka.</p>
            </div>
            {mode === "reset" && (
              <F id="acc-reason" label="Alasan reset *" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Contoh: reseller lupa sandi" />
            )}
            {err && <Notice tone="danger" testId="reseller-account-error">{err}</Notice>}
            <DialogFooter>
              <Button variant="outline" onClick={() => close(false)} data-testid="reseller-account-cancel">Batal</Button>
              <Button onClick={submit} disabled={busy} data-testid="reseller-account-submit">{busy && <Spinner />} {mode === "create" ? "Buat akun" : "Ganti sandi"}</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function PlatformBilling() {
  const qc = useQueryClient();
  const belumDibaca = useUnreadNotifications();
  const perluTindakan = belumDibaca.some((n) => n.link?.startsWith("/pusat-kendali/langganan")
    && (n.kind === "billing_aktivasi" || n.kind === "billing_setoran"));
  const [periode, setPeriode] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [priceFor, setPriceFor] = useState(null);
  const [accountFor, setAccountFor] = useState(null);
  const [blockFor, setBlockFor] = useState(null);
  const [reassignFor, setReassignFor] = useState(null);
  const [targets, setTargets] = useState({});
  const [billSchool, setBillSchool] = useState("all");
  const [busy, setBusy] = useState("");
  const [expanded, setExpanded] = useState({});
  const [picked, setPicked] = useState({});
  const [actionFor, setActionFor] = useState(null);

  // Kunci harus berjenjang ["billing", ...]: React Query hanya mengusir kunci yang diawali
  // elemen persis, sehingga format lama "billing-settings" lolos dari invalidateQueries.
  const settings = useQuery({ queryKey: ["billing", "settings"], queryFn: () => fetcher("/platform/billing/settings") });
  const resellers = useQuery({ queryKey: ["billing", "resellers"], queryFn: () => fetcher("/platform/billing/resellers") });
  const schools = useQuery({ queryKey: ["billing", "schools"], queryFn: () => fetcher("/platform/billing/schools") });
  const active = periode || settings.data?.current_period || "";
  const shadow = useQuery({ queryKey: ["billing", "shadow", active], queryFn: () => fetcher(`/platform/billing/shadow?periode=${active}`), enabled: !!active });
  const recv = useQuery({ queryKey: ["billing", "receivables", active], queryFn: () => fetcher(`/platform/billing/receivables?periode=${active}`), enabled: !!active });
  const bills = useQuery({
    queryKey: ["billing", "bills", active, billSchool],
    queryFn: () => fetcher(`/platform/billing/bills?periode=${active}${billSchool === "all" ? "" : `&school_id=${billSchool}`}&limit=200`),
    enabled: !!active,
  });
  const periodeList = [...new Set([settings.data?.current_period, ...(recv.data?.periods || [])].filter(Boolean))].sort().reverse();
  const lagiMuat = useIsFetching({ queryKey: ["billing"] });

  const reload = async () => { await qc.invalidateQueries({ queryKey: ["billing"] }); };
  const pickedIds = Object.keys(picked).filter((k) => picked[k]);
  const semuaBaris = (bills.data || []).map((b) => b.id);
  const sekolahTerpilih = billSchool !== "all" ? schools.data?.find((sk) => sk.id === billSchool) : null;
  // Baris reseller penampung founder (id tetap dari backend) hanya dipakai untuk menampilkan kanal terima uang.
  const founder = (resellers.data || []).find((r) => r.id === "res_platform");
  const founderBayar = founder
    ? [founder.qris_label && `QRIS ${founder.qris_label}`, founder.qris_nmid, founder.bank_account && `rekening ${founder.bank_account}`].filter(Boolean).join(" · ")
    : "";
  const selesaiAksi = () => {
    setPicked({});
    setActionFor(null);
    reload();
  };

  const run = async () => {
    setBusy("run");
    try {
      const { data } = await api.post("/platform/billing/run", { periode: active });
      toast.success(`Tagihan ${data.periode}: ${data.dibuat} akun baru, ${data.sudah_ada} sudah tertagih, ${data.ditunda_akun_baru} ditunda ke bulan depan.`);
      reload();
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy("");
    }
  };

  const saveSettings = async (values) => {
    setBusy("settings");
    try {
      const { data } = await api.patch("/platform/billing/settings", values);
      toast.success(`Pengaturan tersimpan. Plafon ${rupiah(data.retail_price_cap_idr)}, harga awal ${rupiah(data.default_retail_price_idr)}.`);
      await reload();
    } catch (e) {
      toast.error(errorMessage(e));
      throw e;
    } finally {
      setBusy("");
    }
  };

  const assign = async (reason) => {
    const { data } = await api.patch(`/platform/billing/schools/${reassignFor.school_id}/reseller`, {
      reseller_id: reassignFor.target, reason,
    });
    toast.success(`${reassignFor.school_name} kini dilayani ${data.reseller_name}. Tagihan periode lama tidak berubah.`);
    reload();
  };

  const setBlock = async (reason) => {
    const next = blockFor.status === "Aktif" ? "Diblokir" : "Aktif";
    await api.patch(`/platform/billing/resellers/${blockFor.id}`, { status: next, reason });
    toast.success(`${blockFor.name} ditandai ${next === "Aktif" ? "aktif kembali" : "diblokir"}.`);
    reload();
  };

  if (settings.isLoading) return <LoadingState rows={6} />;
  if (settings.error) return <ErrorState error={settings.error} onRetry={settings.refetch} />;
  const s = settings.data;

  return (
    <div className="space-y-6">
      <PageHeader eyebrow="Pusat Kendali SIST" title="Langganan & Piutang"
        description="Tagihan dihitung per akun untuk satu bulan berjalan. Mulai dari tab Perlu tindakan: aktifkan akun yang sudah dikonfirmasi reseller, lalu verifikasi setoran yang masuk ke founder."
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" onClick={reload} data-testid="billing-refresh-button" title="Ambil angka terbaru dari server">
              <RefreshCw className={`h-4 w-4 mr-1.5 ${lagiMuat ? "animate-spin" : ""}`} /> Muat ulang
            </Button>
            <Select value={active} onValueChange={setPeriode}>
              <SelectTrigger aria-label="Periode tagihan" className="w-40" data-testid="billing-period-select"><SelectValue /></SelectTrigger>
              <SelectContent>{periodeList.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
            <Button onClick={run} disabled={busy === "run"} data-testid="billing-run-button" title={`Susun tagihan ${active}`}>
              {busy === "run" ? <Spinner /> : <Calculator className="h-4 w-4 mr-1.5" />} Hitung tagihan {active}
            </Button>
          </div>
        } />

      <Notice tone={s.enforce_enabled ? "warn" : "info"} testId="billing-mode-notice">
        <div className="flex flex-wrap items-center gap-x-6 gap-y-1">
          <span className="font-semibold">{s.enforce_enabled ? "Pemblokiran aktif" : "Mode bayangan berjalan"}</span>
          <span>Hari ke-{shadow.data?.hari_ini ?? "—"} dari {active}</span>
          <span>Tagihan tersimpan: {s.bills_total}</span>
          <span>Reseller: {resellers.data?.length ?? s.resellers_total}</span>
        </div>
      </Notice>

      <Tabs defaultValue="tindakan" className="space-y-4">
        <TabsList className="flex-wrap">
          <TabsTrigger value="tindakan" data-testid="billing-tab-tindakan">
            Perlu tindakan
            <ActionDot show={perluTindakan} testId="billing-tab-tindakan-dot" className="ml-1.5" />
          </TabsTrigger>
          <TabsTrigger value="ringkasan" data-testid="billing-tab-ringkasan">Ringkasan</TabsTrigger>
          <TabsTrigger value="piutang" data-testid="billing-tab-piutang">Piutang reseller</TabsTrigger>
          <TabsTrigger value="akun" data-testid="billing-tab-akun">Tagihan per akun</TabsTrigger>
          <TabsTrigger value="reseller" data-testid="billing-tab-reseller">Reseller</TabsTrigger>
          <TabsTrigger value="sekolah" data-testid="billing-tab-sekolah">Sekolah &amp; reseller</TabsTrigger>
          <TabsTrigger value="pengaturan" data-testid="billing-tab-pengaturan">Pengaturan</TabsTrigger>
        </TabsList>

        <TabsContent value="tindakan" className="space-y-6">
          <BillingActionQueue periode={active} wholesale={s.wholesale_price_idr} onActivate={setActionFor} />
        </TabsContent>

        <TabsContent value="ringkasan" className="space-y-6">
          <div className="grid grid-cols-2 xl:grid-cols-3 gap-4">
            <Stat label="Akun tertagih" value={shadow.data?.akun_total ?? "—"} hint={`Periode ${active}`} testId="billing-stat-accounts" />
            <Stat label="Dikonfirmasi reseller" value={`${recv.data?.totals.akun_dibayar ?? "—"} akun`}
              hint={`Dari ${recv.data?.totals.akun ?? "—"} akun · lapis sekolah → reseller`} tone="text-emerald-600"
              testId="billing-stat-dikonfirmasi" />
            <Stat label="Menunggu aktivasi founder" value={`${recv.data?.totals.akun_menunggu_aktivasi ?? "—"} akun`}
              hint="Tindak lanjuti di tab Perlu tindakan" tone="text-amber-600" testId="billing-stat-aktivasi" />
            <Stat label="Sudah aktif" value={`${recv.data?.totals.akun_aktif ?? "—"} akun`}
              hint="Lunas dan tidak bisa terkunci tanggal" tone="text-emerald-600" testId="billing-stat-aktif" />
            <Stat label="Setoran menunggu verifikasi" value={uang(recv.data?.totals.setoran_menunggu)}
              hint="Laporan reseller yang belum Anda cocokkan" tone="text-amber-600" testId="billing-stat-setoran-menunggu" />
            <Stat label="Sisa setoran" value={uang(recv.data?.totals.sisa_kulakan)}
              hint={`Harga setoran ${uang(recv.data?.wholesale_price_idr)} per akun aktif`} tone="text-[#E11D2E]"
              testId="billing-stat-receivable" />
          </div>

          <Section title="Proyeksi penguncian akun" testId="billing-shadow-section"
            description={s.enforce_enabled
              ? "Angka di bawah adalah kondisi akses yang berlaku saat ini."
              : "Angka di bawah adalah andaikan pemblokiran dinyalakan. Belum ada satu pun akun yang benar-benar terkunci."}>
            {!shadow.data ? (shadow.isLoading ? <LoadingState rows={2} /> : <ErrorState error={shadow.error} onRetry={shadow.refetch} />) : (
              <div className="space-y-4">
                <div className="flex flex-wrap gap-2">
                  {Object.entries(shadow.data.proyeksi_tanggal_4).map(([state, count]) => (
                    <div key={state} className="rounded-xl border px-4 py-2 flex items-center gap-2" data-testid={`billing-projection-${state}`}>
                      <StatusBadge map={ACCESS_STATE} value={state} />
                      <span className="font-brand font-extrabold">{count}</span>
                    </div>
                  ))}
                </div>
                <p className="text-sm text-muted-foreground">
                  Tanpa pembayaran, tagihan dianggap jatuh tempo pada tanggal {shadow.data.tanggal_terkunci} setiap bulan.
                  Akun petugas gerbang presensi tetap bisa mencetak kehadiran supaya absensi sekolah tidak berhenti.
                </p>
                {!!shadow.data.terkunci_sample.length && (
                  <div className="rounded-xl border border-amber-200 bg-amber-50 dark:bg-amber-950/40 dark:border-amber-900 p-4">
                    <p className="text-sm font-semibold text-amber-900 dark:text-amber-100">Contoh akun yang akan terkunci ({shadow.data.terkunci_sample.length} pertama)</p>
                    <ul className="mt-2 text-sm text-amber-900 dark:text-amber-100 grid md:grid-cols-2 gap-x-6 gap-y-1">
                      {shadow.data.terkunci_sample.map((b) => (
                        <li key={b.bill_id} data-testid={`billing-locked-sample-${b.bill_id}`}>
                          {b.full_name || b.email} · {b.school_name} · {rupiah(b.retail_price_idr)}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </Section>
        </TabsContent>

        <TabsContent value="piutang" className="space-y-6">
          <Section title={`Piutang ${active} per reseller`} testId="billing-receivable-section"
            description="Satu baris per reseller, diurutkan dari sisa setoran terbesar. “Dikonfirmasi reseller” = sekolah sudah membayar ke reseller; “Menunggu aktivasi” = tinggal founder mengaktifkan; “Setoran terverifikasi” = uang setoran yang sudah masuk ke founder. Tekan “Tampilkan sekolah” untuk rincian tiap sekolah.">
            {!recv.data ? (recv.isLoading ? <LoadingState rows={3} /> : <ErrorState error={recv.error} onRetry={recv.refetch} />) : !recv.data.rows.length ? (
              <EmptyState title="Belum ada tagihan pada periode ini" description="Tekan “Hitung tagihan” untuk menyusun tagihan bulan berjalan." />
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Reseller</TableHead>
                      <TableHead className="text-right">Akun</TableHead>
                      <TableHead className="text-right">Dikonfirmasi reseller</TableHead>
                      <TableHead className="text-right">Menunggu aktivasi</TableHead>
                      <TableHead className="text-right">Nilai jual akun</TableHead>
                      <TableHead className="text-right">Setoran terverifikasi</TableHead>
                      <TableHead className="text-right">Sisa setoran</TableHead>
                      <TableHead>Kondisi setor</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {recv.data.rows.map((r) => (
                      <Fragment key={r.reseller_id}>
                        <TableRow data-testid={`billing-receivable-${r.reseller_id}`}>
                          <TableCell>
                            <p className="font-medium">{r.reseller_name}</p>
                            <button type="button" className="text-xs text-[#3B82F6] hover:underline mt-0.5"
                              onClick={() => setExpanded({ ...expanded, [r.reseller_id]: !expanded[r.reseller_id] })}
                              data-testid={`billing-receivable-toggle-${r.reseller_id}`}>
                              {expanded[r.reseller_id] ? "Sembunyikan sekolah" : `Tampilkan ${r.sekolah.length} sekolah`}
                            </button>
                          </TableCell>
                          <TableCell className="text-right tabular-nums">{r.akun}</TableCell>
                          <TableCell className="text-right tabular-nums" data-testid={`billing-receivable-lunas-${r.reseller_id}`}>
                            {r.akun_dibayar} / {r.akun}
                            <span className="block text-xs font-normal text-muted-foreground">{r.akun_aktif} sudah aktif</span>
                          </TableCell>
                          <TableCell className="text-right tabular-nums" data-testid={`billing-receivable-aktivasi-${r.reseller_id}`}>
                            {r.akun_menunggu_aktivasi ? (
                              <span className="font-semibold text-amber-600">{r.akun_menunggu_aktivasi}</span>
                            ) : <span className="text-muted-foreground">—</span>}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">{rupiah(r.nominal_jual)}</TableCell>
                          <TableCell className="text-right tabular-nums">
                            {rupiah(r.terbayar_ke_founder)}
                            {!!r.setoran_menunggu && <span className="block text-xs font-normal text-amber-600">+{rupiah(r.setoran_menunggu)} menunggu</span>}
                          </TableCell>
                          <TableCell className="text-right tabular-nums font-semibold">{rupiah(r.sisa_kulakan)}</TableCell>
                          <TableCell>
                            <span className={`text-xs font-semibold ${r.sisa_kulakan ? "text-[#E11D2E]" : "text-emerald-600"}`}>{r.status_setor}</span>
                          </TableCell>
                        </TableRow>
                        {expanded[r.reseller_id] && (
                          <TableRow key={`${r.reseller_id}-detail`} data-testid={`billing-receivable-detail-${r.reseller_id}`} className="bg-muted/40">
                            <TableCell colSpan={8} className="py-4 pl-6 pr-4">
                              <ul className="grid md:grid-cols-2 gap-x-10 gap-y-2">
                                {r.sekolah.map((sk) => (
                                  <li key={sk.school_id} className="flex items-baseline justify-between gap-4 border-b border-dashed border-border pb-1.5"
                                    data-testid={`billing-receivable-school-${sk.school_id}`}>
                                    <span className="text-sm font-medium">{sk.school_name}</span>
                                    <span className="text-xs text-muted-foreground tabular-nums whitespace-nowrap">
                                      {sk.akun} akun · dikonfirmasi {sk.akun_dibayar} · menunggu aktivasi {sk.akun_menunggu_aktivasi} · aktif {sk.akun_aktif}
                                    </span>
                                  </li>
                                ))}
                              </ul>
                              <p className="text-xs text-muted-foreground mt-3">
                                Angka sekolah adalah tagihan periode {active} dengan harga yang terkunci di baris tagihannya.
                                Setoran dicatat per reseller, sehingga sisa setor belum bisa dipecah per sekolah.
                              </p>
                            </TableCell>
                          </TableRow>
                        )}
                      </Fragment>
                    ))}
                    <TableRow data-testid="billing-receivable-total" className="border-t-2">
                      <TableCell className="font-bold">Total</TableCell>
                      <TableCell className="text-right font-bold tabular-nums">{recv.data.totals.akun}</TableCell>
                      <TableCell className="text-right font-bold tabular-nums" data-testid="billing-receivable-total-lunas">
                        {recv.data.totals.akun_dibayar} / {recv.data.totals.akun}
                      </TableCell>
                      <TableCell className="text-right font-bold tabular-nums text-amber-600">{recv.data.totals.akun_menunggu_aktivasi}</TableCell>
                      <TableCell className="text-right font-bold tabular-nums">{rupiah(recv.data.totals.nominal_jual)}</TableCell>
                      <TableCell className="text-right font-bold tabular-nums">{rupiah(recv.data.totals.terbayar_ke_founder)}</TableCell>
                      <TableCell className="text-right font-bold tabular-nums text-[#E11D2E]">{rupiah(recv.data.totals.sisa_kulakan)}</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            )}
          </Section>
        </TabsContent>

        <TabsContent value="reseller" className="space-y-6">
          <Section title="Daftar reseller" testId="billing-reseller-section"
            description="Harga jual boleh diubah reseller sampai plafon Rp5.000. Buat satu akun login reseller lewat tombol Akun agar mereka bisa masuk ke Ruang Mitra di /mitra. Harga setoran ke founder tidak dapat diubah siapa pun.">
            {!resellers.data ? (resellers.isLoading ? <LoadingState rows={3} /> : <ErrorState error={resellers.error} onRetry={resellers.refetch} />) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Reseller</TableHead>
                      <TableHead>Kontak</TableHead>
                      <TableHead>QRIS</TableHead>
                      <TableHead className="text-right">Harga jual</TableHead>
                      <TableHead className="text-right">Harga setoran</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Aksi</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {resellers.data.map((r) => (
                      <TableRow key={r.id} data-testid={`billing-reseller-row-${r.id}`}>
                        <TableCell>
                          <p className="font-medium">{r.name}</p>
                          <p className="text-xs text-muted-foreground">Kode {r.referral_code}</p>
                          <p className="text-xs text-muted-foreground" data-testid={`billing-reseller-login-${r.id}`}>
                            {r.login_email ? `Akun: ${r.login_email}` : "Belum ada akun login"}
                          </p>
                        </TableCell>
                        <TableCell className="text-sm">
                          {r.contact_name || "—"}<span className="block text-xs text-muted-foreground">{r.phone || r.email || "—"}</span>
                        </TableCell>
                        <TableCell className="text-sm">{r.qris_label || "—"}<span className="block text-xs text-muted-foreground">{r.qris_nmid || "NMID belum diisi"}</span></TableCell>
                        <TableCell className="text-right">{rupiah(r.retail_price_idr)}</TableCell>
                        <TableCell className="text-right text-muted-foreground">{rupiah(r.wholesale_price_idr)}</TableCell>
                        <TableCell><StatusBadge map={RESELLER_STATUS} value={r.status} testId={`billing-reseller-status-${r.id}`} /></TableCell>
                        <TableCell className="text-right whitespace-nowrap">
                          <Button variant="ghost" size="sm" onClick={() => setAccountFor(r)} title="Akun login Ruang Mitra" data-testid={`billing-reseller-account-${r.id}`}>
                            <KeyRound className="h-4 w-4" /> {r.login_email ? "Sandi" : "Akun"}
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => setPriceFor(r)} title="Ubah harga jual" data-testid={`billing-reseller-price-${r.id}`}>
                            <Wallet className="h-4 w-4" /> Harga
                          </Button>
                          {!r.is_platform && (
                            <Button variant="ghost" size="sm" onClick={() => setBlockFor(r)} data-testid={`billing-reseller-block-${r.id}`}>
                              {r.status === "Aktif" ? <Lock className="h-4 w-4" /> : <Unlock className="h-4 w-4" />} {r.status === "Aktif" ? "Blokir" : "Aktifkan"}
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </Section>
        </TabsContent>

        <TabsContent value="sekolah" className="space-y-6">
          <Section title="Reseller setiap sekolah" testId="billing-school-section"
            description="Memindah reseller hanya mengubah tagihan periode berikutnya. Baris tagihan lama tetap menyimpan reseller dan harga saat ia dibuat.">
            {!schools.data ? (schools.isLoading ? <LoadingState rows={3} /> : <ErrorState error={schools.error} onRetry={schools.refetch} />) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Sekolah</TableHead>
                      <TableHead>Status tenant</TableHead>
                      <TableHead>Reseller saat ini</TableHead>
                      <TableHead className="text-right">Pindahkan ke</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {schools.data.map((sk) => (
                      <TableRow key={sk.id} data-testid={`billing-school-row-${sk.id}`}>
                        <TableCell><p className="font-medium">{sk.name}</p><p className="text-xs text-muted-foreground">{sk.city}</p></TableCell>
                        <TableCell><StatusBadge map={TENANT_STATUS} value={sk.status} /></TableCell>
                        <TableCell className="text-sm">{sk.reseller_name}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Select value={targets[sk.id] || sk.reseller_id} onValueChange={(v) => setTargets({ ...targets, [sk.id]: v })}>
                              <SelectTrigger aria-label={`Reseller tujuan ${sk.name}`} className="w-44"><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {(resellers.data || []).map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}
                              </SelectContent>
                            </Select>
                            <Button size="sm" variant="outline" disabled={(targets[sk.id] || sk.reseller_id) === sk.reseller_id}
                              onClick={() => setReassignFor({ school_id: sk.id, school_name: sk.name, target: targets[sk.id] || sk.reseller_id, current: sk.reseller_name })}
                              data-testid={`billing-school-assign-${sk.id}`}>Pindahkan</Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </Section>
        </TabsContent>

        <TabsContent value="akun" className="space-y-6">
          <Section title={`Tagihan per akun ${active}`} testId="billing-bills-section"
            description="Centang satu akun untuk menilai satu akun, centang beberapa untuk menilai sekaligus. Status tagihan: Belum bayar → dikonfirmasi reseller → aktif. Kondisi akses adalah akibat dari status itu ditambah tanggal hari ini."
            actions={
              <div className="flex flex-wrap items-center gap-2">
                <Select value={billSchool} onValueChange={(v) => { setBillSchool(v); setPicked({}); }}>
                  <SelectTrigger aria-label="Saring sekolah" className="w-56" data-testid="billing-bills-school-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Semua sekolah</SelectItem>
                    {(schools.data || []).map((sk) => <SelectItem key={sk.id} value={sk.id}>{sk.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                {sekolahTerpilih && (
                  <Button size="sm" variant="outline" data-testid="billing-collective-button"
                    onClick={() => setActionFor({ action: "CLAIMED", scope: { school_id: sekolahTerpilih.id }, targetLabel: sekolahTerpilih.name })}>
                    Penilaian kolektif {sekolahTerpilih.name}
                  </Button>
                )}
              </div>
            }>
            {!bills.data ? (bills.isLoading ? <LoadingState rows={3} /> : <ErrorState error={bills.error} onRetry={bills.refetch} />) : !bills.data.length ? (
              <EmptyState title="Belum ada tagihan tersusun" description="Tekan “Hitung tagihan” di kanan atas." />
            ) : (
              <div className="overflow-x-auto">
                {!!pickedIds.length && (
                  <div className="mb-3 flex flex-wrap items-center gap-2 rounded-xl border bg-muted/40 px-4 py-3" data-testid="billing-action-bar">
                    <p className="mr-auto text-sm font-medium">{pickedIds.length} akun dipilih</p>
                    {Object.entries(ACTIONS).map(([kode, meta]) => (
                      <Button key={kode} size="sm" variant={meta.bahaya ? "ghost" : "outline"} data-testid={`billing-act-${kode}`}
                        onClick={() => setActionFor({ action: kode, scope: { bill_ids: pickedIds }, jumlah: pickedIds.length })}>
                        {meta.label}
                      </Button>
                    ))}
                    <Button size="sm" variant="ghost" data-testid="billing-clear-picks" onClick={() => setPicked({})}>Kosongkan</Button>
                  </div>
                )}
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10">
                        <input type="checkbox" className="h-4 w-4 cursor-pointer accent-[#3B82F6]"
                          aria-label="Pilih semua tagihan di layar ini" data-testid="billing-pick-all"
                          checked={semuaBaris.length > 0 && semuaBaris.every((id) => picked[id])}
                          onChange={(e) => setPicked(Object.fromEntries(semuaBaris.map((id) => [id, e.target.checked])))} />
                      </TableHead>
                      <TableHead>Akun</TableHead>
                      <TableHead>Sekolah</TableHead>
                      <TableHead>Jenis</TableHead>
                      <TableHead>Reseller</TableHead>
                      <TableHead className="text-right">Nominal</TableHead>
                      <TableHead>Status tagihan</TableHead>
                      <TableHead>Kondisi akses hari ini</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {bills.data.map((b) => (
                      <TableRow key={b.id} data-testid={`billing-bill-row-${b.id}`}>
                        <TableCell>
                          <input type="checkbox" className="h-4 w-4 cursor-pointer accent-[#3B82F6]"
                            data-testid={`billing-pick-${b.id}`}
                            checked={!!picked[b.id]} aria-label={`Pilih tagihan ${b.full_name || b.email}`}
                            onChange={(e) => setPicked({ ...picked, [b.id]: e.target.checked })} />
                        </TableCell>
                        <TableCell><p className="font-medium">{b.full_name || "(nama belum diisi)"}</p><p className="text-xs text-muted-foreground">{b.email}</p></TableCell>
                        <TableCell className="text-sm">{b.school_name}</TableCell>
                        <TableCell className="text-sm">{b.kind === "alumni" ? "Alumni" : "Sekolah"}{b.gate_presensi && <span className="block text-xs text-[#3B82F6]">gerbang presensi</span>}</TableCell>
                        <TableCell className="text-sm">{b.reseller_name}</TableCell>
                        <TableCell className="text-right">{rupiah(b.retail_price_idr)}</TableCell>
                        <TableCell><StatusBadge map={BILL_STATUS} value={b.status} /></TableCell>
                        <TableCell><StatusBadge map={ACCESS_STATE} value={b.access_state} testId={`billing-bill-state-${b.id}`} /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {bills.data.length === 200 && <p className="text-xs text-muted-foreground mt-3">Daftar dipotong pada 200 baris. Saring per sekolah untuk melihat sisanya, atau pakai “Penilaian kolektif” supaya seluruh akun sekolah ikut dinilai.</p>}
              </div>
            )}
          </Section>
        </TabsContent>

        <TabsContent value="pengaturan" className="space-y-6">
          <SettingsCard settings={s} busy={busy === "settings"} onSave={saveSettings} />

          <FounderChannelCard />

          <Section title="Catat setoran & penilaian tagihan" testId="billing-actions-section"
            description="Antrean aktivasi akun dan verifikasi setoran ada di tab Perlu tindakan. Form di bawah dipakai untuk mencatat setoran yang sudah masuk tetapi belum dilaporkan reseller. Penilaian satu akun atau banyak akun dilakukan di tab Tagihan per akun.">
            <div className="grid lg:grid-cols-2 gap-6 items-start">
              <div className="space-y-3 rounded-2xl border p-5" data-testid="billing-how-to-pay">
                <h3 className="text-sm font-bold">Cara pembayaran</h3>
                <ol className="list-decimal pl-5 space-y-2 text-sm text-muted-foreground">
                  <li><span className="font-medium text-foreground">Sekolah → reseller.</span> Sekolah membayar harga jual akunnya ke QRIS reseller yang melayani sekolah itu. Nominal tiap akun terlihat di tab Tagihan per akun.</li>
                  <li><span className="font-medium text-foreground">Reseller → founder.</span> Reseller menyetor {rupiah(s.wholesale_price_idr)} per akun aktif ke founder{founderBayar ? <> lewat {founderBayar}</> : "."}</li>
                  <li><span className="font-medium text-foreground">Pemeriksaan otomatis.</span> Tidak ada unggah bukti transfer. Sistem membandingkan sendiri jumlah setoran dengan jumlah akun yang dinyatakan lunas; kalau selisih, founder kroscek ke reseller sebelum mengubah status.</li>
                </ol>
                <Notice tone={s.enforce_enabled ? "warn" : "muted"} testId="billing-enforce-note">
                  <p className="font-semibold">{s.enforce_enabled ? "Pemblokiran aktif." : "Mode bayangan masih berjalan."}</p>
                  <p className="mt-1">{s.enforce_enabled
                    ? "Penilaian founder langsung menentukan akses akun sekolah."
                    : "Penilaian founder tersimpan dan terlihat di laporan, tetapi belum satu pun login sekolah yang diblokir."}</p>
                </Notice>
              </div>
              <SettlementCard resellerOptions={resellers.data || []} periode={active} wholesale={s.wholesale_price_idr} />
            </div>
          </Section>
        </TabsContent>
      </Tabs>

      <ResellerDialog open={showAdd} onOpenChange={setShowAdd} cap={s.retail_price_cap_idr} onSaved={reload} />
      {/* key memaksa dialog dibuat ulang tiap reseller berbeda, supaya kolom harga tidak mewarisi angka sebelumnya */}
      <PriceDialog key={priceFor?.id ?? "none"} open={!!priceFor} onOpenChange={(o) => !o && setPriceFor(null)} reseller={priceFor} cap={s.retail_price_cap_idr} onSaved={reload} />
      <ResellerAccountDialog key={accountFor?.id ?? "none"} open={!!accountFor} onOpenChange={(o) => !o && setAccountFor(null)} reseller={accountFor} onSaved={reload} />
      <ConfirmReasonDialog open={!!blockFor} onOpenChange={(o) => !o && setBlockFor(null)} danger={blockFor?.status === "Aktif"}
        title={blockFor?.status === "Aktif" ? `Blokir ${blockFor?.name}?` : `Aktifkan kembali ${blockFor?.name}?`}
        description="Pemblokiran reseller tidak menghentikan akses sekolahnya; sekolah tetap belajar memakai SIST."
        confirmLabel={blockFor?.status === "Aktif" ? "Blokir reseller" : "Aktifkan reseller"} onConfirm={setBlock}
        testId="billing-block-dialog" />
      <ConfirmReasonDialog open={!!reassignFor} onOpenChange={(o) => !o && setReassignFor(null)}
        title={`Pindahkan ${reassignFor?.school_name}`}
        description={`Dari ${reassignFor?.current} ke reseller terpilih. Alasan ini tersimpan di log audit dan riwayat reseller sekolah.`}
        confirmLabel="Pindahkan reseller" onConfirm={assign} testId="billing-assign-dialog" />
      <ActionDialog open={!!actionFor} onOpenChange={(o) => !o && setActionFor(null)} action={actionFor?.action}
        jumlah={actionFor?.jumlah} scope={actionFor?.scope} targetLabel={actionFor?.targetLabel || ""}
        rincian={actionFor?.rincian} periode={active} onDone={selesaiAksi}
        bukti={`Periode ${active}. Nama founder dan alasan penilaian tersimpan di log audit.`} />
    </div>
  );
}

// Gambar QRIS founder dikecilkan di browser (JPEG ≤400 ribu karakter) sebelum dikirim,
// pola yang sama dengan halaman Pengaturan Ruang Mitra.
const kecilkanQris = async (berkas) => {
  if (!berkas.type?.startsWith("image/")) throw new Error("Pilih berkas gambar (JPEG atau PNG).");
  const gambar = await createImageBitmap(berkas, { imageOrientation: "from-image" }).catch(() => null);
  if (!gambar) throw new Error("Gambar tidak bisa dibaca. Coba berkas lain.");
  const skala = Math.min(1, 800 / Math.max(gambar.width, gambar.height));
  const kanvas = document.createElement("canvas");
  kanvas.width = Math.round(gambar.width * skala);
  kanvas.height = Math.round(gambar.height * skala);
  const ctx = kanvas.getContext("2d");
  ctx.fillStyle = "#FFFFFF";
  ctx.fillRect(0, 0, kanvas.width, kanvas.height);
  ctx.drawImage(gambar, 0, 0, kanvas.width, kanvas.height);
  gambar.close?.();
  const hasil = kanvas.toDataURL("image/jpeg", 0.85);
  if (hasil.length > 400_000) throw new Error("Gambar masih terlalu besar. Coba gambar QRIS yang lebih sederhana.");
  return hasil;
};

function FounderChannelCard() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ["billing", "founder-qris"], queryFn: () => fetcher("/platform/billing/founder-qris") });
  const [f, setF] = useState(null);
  const [qrisBaru, setQrisBaru] = useState(undefined);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const fileRef = useRef(null);
  if (q.isLoading) return <LoadingState rows={4} />;
  if (q.error) return <ErrorState error={q.error} onRetry={q.refetch} />;
  const d = q.data || {};
  const form = f || { qris_label: d.qris_label || "", qris_nmid: d.qris_nmid || "", bank_account: d.bank_account || "" };
  const set = (k) => (e) => setF({ ...form, [k]: e.target.value });
  const tampil = qrisBaru === null ? null : (qrisBaru || d.qris_image);

  const simpan = async () => {
    setBusy(true);
    setErr("");
    try {
      const body = { qris_label: form.qris_label, qris_nmid: form.qris_nmid, bank_account: form.bank_account,
                     reason: "Founder memperbarui kanal setoran dari Pusat Kendali" };
      if (qrisBaru) body.qris_image = qrisBaru;
      else if (qrisBaru === null && d.qris_image) body.remove_qris_image = true;
      await api.patch("/platform/billing/founder-qris", body);
      toast.success("Kanal bayar founder tersimpan. Reseller melihat kanal ini saat menyetor.");
      setQrisBaru(undefined);
      setF(null);
      await qc.invalidateQueries({ queryKey: ["billing"] });
    } catch (e2) {
      setErr(errorMessage(e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4 rounded-2xl border p-5" data-testid="billing-founder-channel">
      <div>
        <h3 className="text-sm font-bold">Kanal setoran (uang masuk ke founder)</h3>
        <p className="text-sm text-muted-foreground mt-1">Kanal ini ditampilkan ke reseller di halaman Kelola Pembayaran mereka saat menyetor.</p>
      </div>
      <div className="grid md:grid-cols-2 gap-5 items-start">
        <div className="space-y-3">
          <div className="space-y-2">
            <Label htmlFor="fc-label">Label QRIS</Label>
            <Input id="fc-label" placeholder="Strato" value={form.qris_label} onChange={set("qris_label")} data-testid="billing-founder-label" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="fc-nmid">NMID QRIS</Label>
            <Input id="fc-nmid" placeholder="ID1026601636883" value={form.qris_nmid} onChange={set("qris_nmid")} data-testid="billing-founder-nmid" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="fc-bank">Rekening bank</Label>
            <Input id="fc-bank" placeholder="BCA 1234567890 a.n. ..." value={form.bank_account} onChange={set("bank_account")} data-testid="billing-founder-bank" />
          </div>
        </div>
        <div className="space-y-3">
          <div className="rounded-xl border p-3 grid place-items-center bg-muted/40 min-h-40" data-testid="billing-founder-qris-preview">
            {tampil ? <img src={tampil} alt="QRIS founder" className="max-h-48 rounded-lg" /> :
              <p className="text-sm text-muted-foreground py-8">Belum ada gambar QRIS.</p>}
          </div>
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} data-testid="billing-founder-upload">Unggah gambar</Button>
            <input ref={fileRef} type="file" accept="image/*" className="hidden"
              onChange={async (e) => {
                const berkas = e.target.files?.[0];
                e.target.value = "";
                if (!berkas) return;
                try { setQrisBaru(await kecilkanQris(berkas)); setErr(""); }
                catch (ex) { setErr(ex.message); }
              }} data-testid="billing-founder-file" />
            {d.qris_image && qrisBaru === undefined && (
              <Button type="button" variant="ghost" size="sm" className="text-[#E11D2E]" onClick={() => setQrisBaru(null)} data-testid="billing-founder-remove">Hapus gambar</Button>
            )}
            {qrisBaru === null && (
              <Button type="button" variant="ghost" size="sm" onClick={() => setQrisBaru(undefined)}>Batalkan penghapusan</Button>
            )}
            {qrisBaru && (
              <Button type="button" variant="ghost" size="sm" onClick={() => setQrisBaru(undefined)}>Batalkan gambar baru</Button>
            )}
          </div>
        </div>
      </div>
      {err && <Notice tone="danger" testId="billing-founder-error">{err}</Notice>}
      <Button onClick={simpan} disabled={busy} data-testid="billing-founder-submit">{busy && <Spinner />} Simpan kanal founder</Button>
    </div>
  );
}

function SettingsCard({ settings, busy, onSave }) {
  const [cap, setCap] = useState(String(settings.retail_price_cap_idr));
  const [retail, setRetail] = useState(String(settings.default_retail_price_idr));
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!/^\d+$/.test(cap) || !/^\d+$/.test(retail)) return setErr("Plafon dan harga awal harus angka rupiah tanpa titik, contoh 5000.");
    setErr("");
    try {
      await onSave({ retail_price_cap_idr: Number(cap), default_retail_price_idr: Number(retail),
        reason: "Diubah dari Pusat Kendali" });
    } catch (e2) {
      setErr(errorMessage(e2));
    }
  };

  return (
    <Section title="Harga standar langganan" testId="billing-settings-section"
      description="Berlaku untuk tagihan periode berikutnya. Tagihan yang sudah tersusun tidak pernah berubah angkanya.">
      <form className="grid md:grid-cols-2 gap-5 max-w-3xl" onSubmit={submit} noValidate data-testid="billing-settings-form">
        <F id="cap" label="Plafon harga jual per akun" hint="Reseller tidak bisa memasang harga di atas angka ini." value={cap} onChange={(e) => setCap(e.target.value)} inputMode="numeric" data-testid="billing-cap-input" />
        <F id="retail" label="Harga jual awal per akun" hint={`Awal resmi Rp3.000. Harga setoran ke founder tetap ${rupiah(settings.wholesale_price_idr)} dan tidak bisa diubah.`} value={retail} onChange={(e) => setRetail(e.target.value)} inputMode="numeric" data-testid="billing-retail-input" />
        <div className="md:col-span-2 space-y-3">
          {err && <Notice tone="danger" testId="billing-settings-error">{err}</Notice>}
          <p className="text-xs text-muted-foreground">Terakhir diubah {settings.updated_at ? new Date(settings.updated_at).toLocaleString("id-ID", { timeZone: "Asia/Jakarta" }) : "belum pernah"} WIB{settings.updated_by && <> oleh {settings.updated_by}</>}.</p>
          <Button type="submit" disabled={busy} data-testid="billing-settings-submit">{busy && <Spinner />} Simpan pengaturan</Button>
        </div>
      </form>
    </Section>
  );
}

function SettlementCard({ resellerOptions, periode, wholesale }) {
  const qc = useQueryClient();
  const [rid, setRid] = useState("");
  const [amount, setAmount] = useState("");
  const [metode, setMetode] = useState("transfer");
  const [catatan, setCatatan] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const [cancelFor, setCancelFor] = useState(null);

  const pilihan = rid || resellerOptions[0]?.id || "";
  const laporan = useQuery({
    queryKey: ["billing", "settlement", pilihan, periode],
    queryFn: () => fetcher(`/platform/billing/settlements?reseller_id=${encodeURIComponent(pilihan)}&periode=${periode}`),
    enabled: !!pilihan && !!periode,
  });
  const tarikUlang = () => qc.invalidateQueries({ queryKey: ["billing"] });
  const g = laporan.data?.gambaran;
  const setoran = laporan.data?.setoran || [];
  const nama = resellerOptions.find((r) => r.id === pilihan)?.name || "reseller";

  const simpan = async (e) => {
    e.preventDefault();
    if (!/^\d{3,}$/.test(amount)) return setErr("Jumlah setoran ditulis sebagai angka rupiah tanpa titik, contoh 140000.");
    setBusy(true);
    setErr("");
    try {
      const { data } = await api.post("/platform/billing/settlements", {
        reseller_id: pilihan, periode, amount_idr: Number(amount), metode, catatan: catatan.trim() || null,
      });
      toast.success(`Setoran ${rupiah(data.setoran.amount_idr)} dari ${nama} tercatat untuk ${periode}.`);
      setAmount("");
      setCatatan("");
      tarikUlang();
    } catch (e2) {
      setErr(errorMessage(e2));
    } finally {
      setBusy(false);
    }
  };

  const batalkan = async (reason) => {
    await api.post(`/platform/billing/settlements/${cancelFor.id}/cancel`, { reason });
    toast.success("Setoran dibatalkan dan tidak dihitung lagi sebagai uang masuk.");
    tarikUlang();
  };

  const verifikasi = async (s) => {
    setBusy("verify");
    try {
      const { data } = await api.post(`/platform/billing/settlements/${s.id}/verify`);
      toast.success(`Setoran ${rupiah(s.amount_idr)} dari ${nama} diverifikasi. Sisa setoran ${rupiah(data.gambaran.sisa_kulakan)}.`);
      tarikUlang();
    } catch (e2) {
      toast.error(errorMessage(e2));
    } finally {
      setBusy("");
    }
  };

  if (!resellerOptions.length) return <EmptyState title="Belum ada reseller" description="Tambahkan reseller lebih dulu di tab Reseller." />;

  return (
    <div className="space-y-4 rounded-2xl border p-5" data-testid="billing-settlement-card">
      <div>
        <h3 className="text-sm font-bold">Catat setoran reseller</h3>
        <p className="text-sm text-muted-foreground mt-1">Harga setoran {rupiah(wholesale)} per akun aktif. Mencatat setoran tidak mengubah status akun — itu tindakan terpisah di tab Tagihan per akun.</p>
      </div>

      <form className="grid sm:grid-cols-2 gap-4" onSubmit={simpan} noValidate data-testid="billing-settlement-form">
        <div className="space-y-2">
          <Label htmlFor="st-reseller">Reseller</Label>
          <Select value={pilihan} onValueChange={setRid}>
            <SelectTrigger id="st-reseller" aria-label="Reseller penyetor" data-testid="billing-settlement-reseller"><SelectValue /></SelectTrigger>
            <SelectContent>{resellerOptions.map((r) => <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="st-metode">Melalui</Label>
          <Select value={metode} onValueChange={setMetode}>
            <SelectTrigger id="st-metode" aria-label="Metode setoran" data-testid="billing-settlement-metode"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="transfer">Transfer bank</SelectItem>
              <SelectItem value="qris">QRIS</SelectItem>
              <SelectItem value="tunai">Tunai</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label htmlFor="st-amount">Jumlah masuk (Rp)</Label>
          <Input id="st-amount" inputMode="numeric" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="140000" data-testid="billing-settlement-amount" />
        </div>
        <div className="space-y-2">
          <Label htmlFor="st-catatan">Catatan</Label>
          <Input id="st-catatan" value={catatan} onChange={(e) => setCatatan(e.target.value)} placeholder="Contoh: setor sebagian, sisanya akhir bulan" data-testid="billing-settlement-note" />
        </div>
        <div className="sm:col-span-2 flex flex-wrap items-center gap-2">
          {g?.sisa_kulakan > 0 && (
            <Button type="button" size="sm" variant="ghost" data-testid="billing-settlement-fill" onClick={() => setAmount(String(g.sisa_kulakan))}>
              Isi sesuai sisa {rupiah(g.sisa_kulakan)}
            </Button>
          )}
          <Button type="submit" disabled={busy} data-testid="billing-settlement-submit">{busy && <Spinner />} Catat setoran</Button>
        </div>
        {err && <div className="sm:col-span-2"><Notice tone="danger" testId="billing-settlement-error">{err}</Notice></div>}
      </form>

      {laporan.isLoading ? <LoadingState rows={2} /> : g && (
        <div className="rounded-xl border bg-muted/30 p-4 space-y-2 text-sm" data-testid="billing-settlement-view" data-periode={periode}>
          <p className="font-semibold">{g.akun} akun ditagihkan ke {nama}</p>
          <div className="grid grid-cols-2 gap-y-1">
            <span className="text-muted-foreground">Harus sampai founder</span><span className="text-right tabular-nums">{rupiah(g.nominal_kulakan)}</span>
            <span className="text-muted-foreground">Tercatat masuk</span><span className="text-right tabular-nums">{rupiah(g.terbayar_ke_founder)}</span>
            <span className="text-muted-foreground">Sisa setor</span>
            <span className={`text-right tabular-nums font-semibold ${g.sisa_kulakan ? "text-[#E11D2E]" : "text-emerald-600"}`} data-testid="billing-settlement-sisa">{rupiah(g.sisa_kulakan)}</span>
            <span className="text-muted-foreground">Setoran setara</span><span className="text-right tabular-nums">{g.akun_tercakup_oleh_setoran} akun</span>
            <span className="text-muted-foreground">Akun dinyatakan bayar ke reseller</span><span className="text-right tabular-nums">{g.akun_dibayar_ke_reseller} akun</span>
          </div>
          {!!g.selisih_akun && (
            <Notice tone="warn" testId="billing-settlement-mismatch">
              Selisih {Math.abs(g.selisih_akun)} akun antara laporan bayar dan uang yang masuk.
              {g.selisih_akun > 0 ? " Reseller melaporkan lebih banyak akun lunas daripada yang terbayar — kroscek dulu sebelum mengaktifkan akun."
                                  : " Uang masuk lebih besar daripada akun yang dilaporkan — periksa apakah ada setoran untuk periode lain."}
            </Notice>
          )}
        </div>
      )}

      {!!setoran.length && (
        <div className="space-y-1">
          <p className="text-sm font-semibold">Riwayat setoran {nama} · {periode}</p>
          <ul className="text-sm">
            {setoran.map((s) => (
              <li key={s.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-dashed py-2" data-testid={`billing-settlement-row-${s.id}`}>
                <span className="tabular-nums font-medium">{rupiah(s.amount_idr)}</span>
                {s.rincian?.school_name && <span className="text-xs rounded-full border px-2 py-0.5 bg-muted/40" data-testid={`billing-settlement-school-${s.id}`}>{s.rincian.school_name}</span>}
                <span className="text-muted-foreground">{s.metode} · {new Date(s.at).toLocaleString("id-ID", { timeZone: "Asia/Jakarta" })} WIB</span>
                {s.catatan && <span className="text-muted-foreground">— {s.catatan}</span>}
                {s.sumber === "reseller" && <span className="text-xs rounded-full border px-2 py-0.5 text-muted-foreground" data-testid={`billing-settlement-source-${s.id}`}>laporan reseller</span>}
                <StatusBadge map={SETTLEMENT_STATUS} value={s.status} />
                {s.status === "menunggu_verifikasi" && (
                  <Button variant="ghost" size="sm" className="text-emerald-600" disabled={busy === "verify"}
                    onClick={() => verifikasi(s)} data-testid={`billing-settlement-verify-${s.id}`}>
                    {busy === "verify" && <Spinner />} Verifikasi
                  </Button>
                )}
                <Button variant="ghost" size="sm" className="ml-auto" data-testid={`billing-settlement-cancel-${s.id}`}
                  onClick={() => setCancelFor(s)}>Salah catat</Button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <ConfirmReasonDialog open={!!cancelFor} onOpenChange={(o) => !o && setCancelFor(null)} danger
        title={`Batalkan setoran ${cancelFor ? rupiah(cancelFor.amount_idr) : ""}?`}
        description="Setorannya hilang dari hitungan piutang. Status akun yang sudah terlanjur diaktifkan tidak ikut berubah — nilai ulang di tab Tagihan per akun."
        confirmLabel="Batalkan setoran" onConfirm={batalkan} testId="billing-settlement-cancel-dialog" />
    </div>
  );
}
